import { getBrowserTimeZone } from "@client-shared/lib/timeZone";
import { useSFDCAuthState } from "@client-shared/providers/auth";
import * as api from "@client-shared/services/api/admin-product";
import type { AdminAttributeUpdateRequest, AdminProductUpdateResponse, AdminStatusChangeRequest } from "@shared/types/admin-product-update";
import * as React from "react";

/** A request as the page builds it; the provider adds the manager's time zone. */
export type AdminAttributeUpdateDraft = Omit<AdminAttributeUpdateRequest, "timeZone">;
export type AdminStatusChangeDraft = Omit<AdminStatusChangeRequest, "timeZone">;

export interface AdminProductUpdatesContextValue {
  submitAttributeUpdate: (draft: AdminAttributeUpdateDraft) => Promise<AdminProductUpdateResponse>;
  submitStatusChange: (draft: AdminStatusChangeDraft) => Promise<AdminProductUpdateResponse>;
}

/** The HTTP calls behind the provider; stories and tests inject stubs here, the app always uses the live API. */
export interface AdminProductUpdateApi {
  postAdminAttributeUpdate: typeof api.postAdminAttributeUpdate;
  postAdminStatusChange: typeof api.postAdminStatusChange;
}

const LIVE_API: AdminProductUpdateApi = {
  postAdminAttributeUpdate: api.postAdminAttributeUpdate,
  postAdminStatusChange: api.postAdminStatusChange,
};

const missingProvider = () => Promise.reject(new Error("AdminProductUpdatesProvider is not mounted"));

export const AdminProductUpdatesContext = React.createContext<AdminProductUpdatesContextValue>({
  submitAttributeUpdate: missingProvider,
  submitStatusChange: missingProvider,
});

/**
 * Beanz Manager product updates for the admin catalogue. Each update is raised in Salesforce as an approval request
 * under the signed-in manager, so it needs their Salesforce token; the backend reads the requestor from the token.
 * New admin update types belong here alongside the existing two.
 */
interface AdminProductUpdatesProviderProps {
  readonly children: React.ReactNode;
  /** Defaults to the live API. */
  readonly api?: AdminProductUpdateApi;
}

export const AdminProductUpdatesProvider: React.FC<AdminProductUpdatesProviderProps> = ({ children, api: updateApi = LIVE_API }) => {
  const [authState] = useSFDCAuthState();
  const accessToken = authState?.sfdcAuthToken;

  const requireToken = React.useCallback((): string => {
    if (!accessToken) throw new Error("Cannot submit an admin update: missing Salesforce access token");
    return accessToken;
  }, [accessToken]);

  const value = React.useMemo<AdminProductUpdatesContextValue>(
    () => ({
      submitAttributeUpdate: async (draft) =>
        updateApi.postAdminAttributeUpdate(requireToken(), { ...draft, timeZone: getBrowserTimeZone() }),
      submitStatusChange: async (draft) => updateApi.postAdminStatusChange(requireToken(), { ...draft, timeZone: getBrowserTimeZone() }),
    }),
    [requireToken, updateApi]
  );

  return <AdminProductUpdatesContext.Provider value={value}>{children}</AdminProductUpdatesContext.Provider>;
};
